import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  InputNumber,
  Popconfirm,
  Space,
  Typography,
  message,
} from 'antd';

import {
  getRetentionPolicies,
  runRetentionCleanup,
  updateRetentionPolicies,
} from '../../services/retentionSettingsService';
import { RetentionCleanupResult, RetentionPoliciesDto } from '../../types/RetentionSettings';
import { sanitizeApiError } from '../../utils/apiErrorSanitizer';
import { SkeletonFieldsetGroup, SkeletonRegion } from '../common/Skeleton/Skeleton';

/** This panel is not yet localized; a minimal i18next-shaped `t` lets it reuse
 * the shared sanitizer (which never falls back to a raw error string). */
const t = ((_key: string, options?: { defaultValue?: string }) => options?.defaultValue ?? '') as any;

interface PolicyField {
  key: string;
  label: string;
  description: string;
}

interface PolicySection {
  title: string;
  fields: PolicyField[];
}

const POLICY_SECTIONS: PolicySection[] = [
  {
    title: 'Actions',
    fields: [
      { key: 'retention.actionHistoryDays', label: 'Action history', description: 'Completed action runs and target results.' },
      { key: 'retention.actionTimelineDays', label: 'Action execution timeline', description: 'Detailed per-target execution events.' },
      { key: 'retention.actionArtifactDays', label: 'Action files', description: 'Uploaded and downloaded action artifacts.' },
      { key: 'retention.actionAuditDays', label: 'Action accountability audit', description: 'Launch, denial, cancellation, retry, and outcome audit events.' },
    ],
  },
  {
    title: 'Security and platform',
    fields: [
      { key: 'retention.securityAdminAuditDays', label: 'Security and administration audit', description: 'Authentication and administration events.' },
      { key: 'retention.platformOperationalAuditDays', label: 'Platform operational audit', description: 'MQTT, ingestion, and platform transition events.' },
      { key: 'retention.notificationHistoryDays', label: 'Notification history', description: 'Stored action and system notifications.' },
    ],
  },
  {
    title: 'RTU and ingestion',
    fields: [
      { key: 'retention.rtuOperationalHistoryDays', label: 'RTU operational history', description: 'Status, reachability, ping, event, and version history.' },
      { key: 'retention.unresolvedQuarantineDays', label: 'Unresolved quarantine', description: 'Quarantine rows and their managed backup files.' },
      { key: 'retention.resolvedQuarantineDays', label: 'Resolved quarantine', description: 'Redriven or otherwise resolved quarantine rows.' },
      { key: 'retention.ingestionBackupDays', label: 'Ingestion backup files', description: 'Ingestion ZIP backup folders.' },
      { key: 'retention.rawRtuFileDays', label: 'Raw RTU files', description: 'Received real and simulator RTU files.' },
    ],
  },
  {
    title: 'Application',
    fields: [
      { key: 'retention.applicationLogDays', label: 'Application log files', description: 'Rolled application, warning, stale, and audit logs.' },
    ],
  },
];

const formatDate = (value?: string | null): string => {
  if (!value) return 'Not yet saved';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not yet saved' : parsed.toLocaleString();
};

const deletedSummary = (result: RetentionCleanupResult): string => {
  if (result.alreadyRunning) return 'A retention cleanup is already running.';
  if (!result.successful) return result.message || 'Retention cleanup did not complete.';
  const total = Object.values(result.deleted ?? {}).reduce((sum, value) => sum + Number(value || 0), 0);
  return `Cleanup completed. ${total.toLocaleString()} expired records or files were removed.`;
};

const PolicyRow = ({ field, disabled }: { field: PolicyField; disabled: boolean }) => (
  <div className="retention-policies__row">
    <div className="retention-policies__row-text">
      <span className="retention-policies__row-label">{field.label}</span>
      <span className="retention-policies__row-description">{field.description}</span>
    </div>
    <Form.Item
      name={field.key}
      className="retention-policies__row-control"
      rules={[
        { required: true, message: `${field.label} is required` },
        { type: 'number', min: 1, max: 3650, message: 'Use a value between 1 and 3650 days' },
      ]}
    >
      <InputNumber min={1} max={3650} addonAfter="days" disabled={disabled} aria-label={field.label} />
    </Form.Item>
  </div>
);

const RetentionPoliciesPanel = () => {
  const [form] = Form.useForm<Record<string, number>>();
  const [snapshot, setSnapshot] = useState<RetentionPoliciesDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRetentionPolicies()
      .then((response) => {
        if (cancelled) return;
        setSnapshot(response);
        form.setFieldsValue(response.policies);
        setDirty(false);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(sanitizeApiError(loadError, { endpointCategory: 'retention-load', t }).message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form]);

  const policyKeys = useMemo(() => POLICY_SECTIONS.flatMap((section) => section.fields.map((field) => field.key)), []);

  const save = async () => {
    const values = await form.validateFields();
    const policies = Object.fromEntries(policyKeys.map((key) => [key, Number(values[key])]));
    setSaving(true);
    try {
      const updated = await updateRetentionPolicies({ policies });
      setSnapshot(updated);
      form.setFieldsValue(updated.policies);
      setDirty(false);
      void message.success('Retention policies saved');
    } catch (saveError: any) {
      void message.error(sanitizeApiError(saveError, { endpointCategory: 'retention-save', t }).message);
    } finally {
      setSaving(false);
    }
  };

  const runCleanup = async () => {
    setRunning(true);
    try {
      const result = await runRetentionCleanup();
      if (result.successful) {
        void message.success(deletedSummary(result), 6);
      } else if (result.alreadyRunning) {
        void message.info(deletedSummary(result), 6);
      } else {
        void message.error(deletedSummary(result), 8);
      }
    } catch (cleanupError: any) {
      void message.error(sanitizeApiError(cleanupError, { endpointCategory: 'retention-cleanup', t }).message);
    } finally {
      setRunning(false);
    }
  };

  const showSkeleton = !snapshot && !error;

  return (
    <div className="admin-settings-retention">
      <Card size="small" className="retention-policies__card">
        <Space direction="vertical" size={16} className="admin-settings-retention__stack">
          {error ? <Alert type="warning" showIcon message={error} /> : null}
          {showSkeleton ? (
            <SkeletonRegion label="Loading retention policies">
              <SkeletonFieldsetGroup groups={POLICY_SECTIONS.map((section) => section.fields.length)} />
            </SkeletonRegion>
          ) : snapshot ? (
            <Form
              form={form}
              layout="vertical"
              className="admin-settings-retention__form"
              onValuesChange={() => setDirty(true)}
            >
              {POLICY_SECTIONS.map((section) => (
                <fieldset className="retention-policies__section" key={section.title}>
                  <legend className="retention-policies__section-title">{section.title}</legend>
                  <div className="retention-policies__section-rows">
                    {section.fields.map((field) => (
                      <PolicyRow key={field.key} field={field} disabled={loading} />
                    ))}
                  </div>
                </fieldset>
              ))}
              <div className="admin-settings-retention__footer">
                <Space>
                  <Button type="primary" loading={saving} disabled={!dirty || loading} onClick={save}>
                    Save policies
                  </Button>
                  <Popconfirm
                    title="Run retention cleanup now?"
                    description="Expired data and managed files will be permanently deleted using the saved policies."
                    okText="Run cleanup"
                    okButtonProps={{ danger: true }}
                    onConfirm={runCleanup}
                  >
                    <Button danger loading={running} disabled={loading || saving}>
                      Run cleanup now
                    </Button>
                  </Popconfirm>
                </Space>
                <Typography.Text type="secondary">
                  Last saved: {formatDate(snapshot?.updatedAtUtc)}
                </Typography.Text>
              </div>
            </Form>
          ) : null}
        </Space>
      </Card>
    </div>
  );
};

export default RetentionPoliciesPanel;
